import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, error, optional, optionalLabel, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold tracking-[0.15em] uppercase text-slate-500 mb-2">
      {label} {optional && <span className="normal-case tracking-normal font-normal text-slate-400">({optionalLabel})</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="mt-1.5 text-xs text-red-500" role="alert" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

export const LeadForm = ({ variant = "contact", defaultInterest = "" }) => {
  const { t, lang } = useLang();
  const isContact = variant === "contact";
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "", website: "",
    interest: defaultInterest, market: "", message: "", consent: false, hp_field: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const idp = `${variant}-form`;

  const interestOptions = [
    ["distribution", t.contact.interests.distribution],
    ["private-label", t.contact.interests.privateLabel],
    ["product-sourcing", t.contact.interests.sourcing],
    ["market-development", t.contact.interests.marketDev],
    ["general-partnership", t.contact.interests.general],
    ...(isContact ? [["product", t.contact.interests.product], ["technical", t.contact.interests.technical]] : []),
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t.contact.errorRequired;
    if (!form.email.trim()) errs.email = t.contact.errorRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) errs.email = t.contact.errorEmail;
    if (!form.interest) errs.interest = t.contact.errorRequired;
    if (!form.message.trim()) errs.message = t.contact.errorRequired;
    if (isContact && !form.country.trim()) errs.country = t.contact.errorRequired;
    if (!form.consent) errs.consent = t.contact.errorConsent;
    return errs;
  };

  const aria = (key) => ({
    "aria-invalid": !!errors[key],
    "aria-describedby": errors[key] ? `${idp}-${key}-error` : undefined,
  });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, { ...form, form_type: isContact ? "contact" : "partnership", language: lang });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="border border-[#19C7C1]/40 bg-[#DFF7F5]/40 p-10 md:p-14 text-center" data-testid="form-success">
        <CheckCircle2 size={36} strokeWidth={1.5} className="text-[#19C7C1] mx-auto" />
        <h3 className="mt-5 text-xl md:text-2xl font-semibold text-[#061A26]">{t.contact.successTitle}</h3>
        <p className="mt-3 text-sm text-slate-600">{t.contact.successCopy}</p>
        <button
          onClick={() => { setStatus("idle"); setForm((f) => ({ ...f, message: "", consent: false })); }}
          className="link-accent mt-7"
          data-testid="form-send-another-btn"
        >
          {t.contact.successAnother}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-7" data-testid={`lead-form-${variant}`}>
      <input type="text" value={form.hp_field} onChange={set("hp_field")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <Field id={`${idp}-name`} label={t.contact.name} error={errors.name}>
          <input id={`${idp}-name`} type="text" value={form.name} onChange={set("name")} className="field-input" data-testid="form-name-input" autoComplete="name" {...aria("name")} />
        </Field>
        <Field id={`${idp}-company`} label={t.contact.company} optional optionalLabel={t.contact.optional}>
          <input id={`${idp}-company`} type="text" value={form.company} onChange={set("company")} className="field-input" data-testid="form-company-input" autoComplete="organization" />
        </Field>
        <Field id={`${idp}-email`} label={t.contact.email} error={errors.email}>
          <input id={`${idp}-email`} type="email" value={form.email} onChange={set("email")} className="field-input" data-testid="form-email-input" autoComplete="email" {...aria("email")} />
        </Field>
        <Field id={`${idp}-country`} label={t.contact.country} error={errors.country} optional={!isContact} optionalLabel={t.contact.optional}>
          <input id={`${idp}-country`} type="text" value={form.country} onChange={set("country")} className="field-input" data-testid="form-country-input" autoComplete="country-name" {...aria("country")} />
        </Field>
        {isContact && (
          <Field id={`${idp}-website`} label={t.contact.website} optional optionalLabel={t.contact.optional}>
            <input id={`${idp}-website`} type="url" value={form.website} onChange={set("website")} className="field-input" data-testid="form-website-input" placeholder="https://" />
          </Field>
        )}
        <Field id={`${idp}-interest`} label={t.contact.interest} error={errors.interest}>
          <select id={`${idp}-interest`} value={form.interest} onChange={set("interest")} className="field-input" data-testid="form-interest-select" {...aria("interest")}>
            <option value="" disabled>{t.contact.selectPlaceholder}</option>
            {interestOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        {isContact && (
          <Field id={`${idp}-market`} label={t.contact.market} optional optionalLabel={t.contact.optional}>
            <input id={`${idp}-market`} type="text" value={form.market} onChange={set("market")} className="field-input" data-testid="form-market-input" />
          </Field>
        )}
      </div>

      <Field id={`${idp}-message`} label={t.contact.message} error={errors.message}>
        <textarea id={`${idp}-message`} value={form.message} onChange={set("message")} rows={5} className="field-input resize-y" data-testid="form-message-input" {...aria("message")} />
      </Field>

      <div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            className="mt-0.5 w-4 h-4 accent-[#19C7C1]"
            data-testid="form-consent-checkbox"
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? `${idp}-consent-error` : undefined}
          />
          <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-800 transition-colors duration-300">{t.contact.consent}</span>
        </label>
        {errors.consent && (
          <p id={`${idp}-consent-error`} className="mt-1.5 text-xs text-red-500" role="alert" data-testid="form-consent-error">{errors.consent}</p>
        )}
      </div>

      {status === "error" && (
        <p className="text-sm text-red-500 border border-red-200 bg-red-50 p-4" role="alert" data-testid="form-submit-error">{t.contact.errorSubmit}</p>
      )}

      <button type="submit" disabled={status === "loading"} className="btn-dark disabled:opacity-60" data-testid="form-submit-btn">
        {status === "loading" ? (<><Loader2 size={16} className="animate-spin" />{t.contact.sending}</>) : t.contact.submit}
      </button>
    </form>
  );
};
